import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslations } from '../hooks/useTranslations';

const BenchmarkingModule = ({ result, formData }) => {
  // Get translations and language context
  const { t, language } = useTranslations();
  
  // Currency formatting function for different locales
  const formatCurrency = (value) => {
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    return new Intl.NumberFormat(locale, { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Percentage formatting function
  const formatPercentage = (value) => {
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    return new Intl.NumberFormat(locale, { 
      style: 'percent', 
      maximumFractionDigits: 1 
    }).format(value / 100);
  };

  // Format impact potential for opportunities (e.g., "+15% valuation")
  const formatImpactPotential = (impactText) => {
    if (language !== 'en' || !impactText) return impactText;
    
    // Handle Spanish impact potential translations
    const impactMappings = {
      '+15% valoración': '+15% valuation',
      '+25% valoración': '+25% valuation', 
      '+35% valoración': '+35% valuation',
      '+20% valoración': '+20% valuation',
      '+30% valoración': '+30% valuation',
      '+40% valoración': '+40% valuation'
    };
    
    return impactMappings[impactText] || impactText.replace(/valoración/g, 'valuation');
  };

  // Date formatting function
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Invalid Date' || isNaN(new Date(dateString).getTime())) {
      return null;
    }
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    const options = language === 'en' 
      ? { year: 'numeric', month: 'short', day: '2-digit' }
      : { year: 'numeric', month: 'short', day: '2-digit' };
    return new Intl.DateTimeFormat(locale, options).format(new Date(dateString));
  };

  // Normalize data structure to handle both traditional and live analysis formats
  const normalizeData = (data) => {
    if (!data) return null;
    
    // If data already has the traditional structure, return as-is
    if (data.competitive_position && data.competitor_comparison) {
      return data;
    }
    
    // If it's the new live analysis format, convert to traditional structure
    if (data.competitors && data.marketPosition !== undefined) {
      // Defensive programming: ensure arrays and values exist
      const competitors = Array.isArray(data.competitors) ? data.competitors : [];
      const marketPosition = data.marketPosition || 1;
      const marketShare = data.marketShare || 10.0;
      
      // Calculate percentile safely
      const percentile = competitors.length > 0 
        ? Math.round((competitors.length - marketPosition + 1) / competitors.length * 100)
        : 75; // Default percentile
      
      // Determine grade based on position
      const grade = marketPosition <= 1 ? 'A+' : 
                    marketPosition <= 2 ? 'A' :
                    marketPosition <= 3 ? 'B+' : 'B';
      
      // Calculate revenue multiple safely
      const firstCompetitor = competitors[0] || {};
      const estimatedValue = firstCompetitor.estimatedValue || 1000000;
      const revenue = firstCompetitor.revenue || 400000;
      const currentMultiple = revenue > 0 ? estimatedValue / revenue : 2.5;
      
      return {
        competitive_position: {
          market_position: {
            percentile: percentile
          },
          competitive_score: {
            grade: grade,
            description: marketPosition <= 2 ? 'Líder del mercado' : 'Posición competitiva sólida'
          },
          financial_metrics: {
            current_multiple: currentMultiple
          }
        },
        competitor_comparison: {
          competitors: competitors.map((comp, index) => ({
            name: comp.name || `Competidor ${index + 1}`,
            valuation: comp.estimatedValue || 500000,
            market_share: comp.marketShare || 5.0,
            growth_rate: (comp.digitalPresence || 50) / 10, // Convert to growth rate
            type: comp.position === 'above' ? 'market_leader' : 'peer_competitor',
            strengths: Array.isArray(comp.strengths) ? comp.strengths : ['Experiencia sectorial']
          })).concat([{
            name: 'Tu Empresa',
            valuation: estimatedValue,
            market_share: marketShare,
            growth_rate: 15,
            type: 'user_company',
            strengths: ['Potencial de crecimiento', 'Agilidad competitiva']
          }]),
          user_position: marketPosition,
          total_competitors_analyzed: competitors.length || 4
        },
        ai_insights: {
          strengths: ['Posicionamiento competitivo', 'Oportunidades de mercado'],
          weaknesses: Array.isArray(data.threats) ? data.threats : ['Competencia sectorial'],
          opportunities: Array.isArray(data.opportunities) ? data.opportunities : ['Crecimiento del mercado'],
          market_trends: Array.isArray(data.competitiveInsights) ? data.competitiveInsights : ['Análisis en desarrollo']
        },
        improvement_opportunities: (Array.isArray(data.strategicRecommendations) ? data.strategicRecommendations : [
          'Fortalecer presencia digital',
          'Mejorar diferenciación competitiva',
          'Expandir base de clientes'
        ]).map((rec, index) => ({
          title: rec,
          description: `Recomendación estratégica basada en análisis competitivo.`,
          priority: index === 0 ? 'alta' : index === 1 ? 'media' : 'baja',
          impact_potential: `+${15 + index * 10}% valoración`,
          time_horizon: `${6 + index * 3}-${12 + index * 3} meses`,
          estimated_investment: index === 0 ? 'Alto' : 'Medio'
        })),
        sector_overview: {
          market_size: 'Crecimiento sostenido',
          growth_rate: 12.5,
          key_trends: Array.isArray(data.competitiveInsights) ? data.competitiveInsights : ['Digitalización del sector']
        },
        generated_at: data.lastUpdatedAt,
        source: data.source || 'live_analysis'
      };
    }
    
    // If no recognizable structure, create minimal fallback
    return {
      competitive_position: {
        market_position: {
          percentile: 65
        },
        competitive_score: {
          grade: 'B+',
          description: 'Posición competitiva sólida'
        },
        financial_metrics: {
          current_multiple: 2.5
        }
      },
      competitor_comparison: {
        competitors: [{
          name: 'Competidor Principal',
          valuation: 800000,
          market_share: 15.0,
          growth_rate: 12.0,
          type: 'market_leader',
          strengths: ['Liderazgo del mercado']
        }, {
          name: 'Tu Empresa',
          valuation: 500000,
          market_share: 10.0,
          growth_rate: 15.0,
          type: 'user_company',
          strengths: ['Potencial de crecimiento']
        }],
        user_position: 2,
        total_competitors_analyzed: 4
      },
      ai_insights: {
        strengths: ['Potencial de crecimiento'],
        weaknesses: ['Competencia intensa'],
        opportunities: ['Expansión digital'],
        market_trends: ['Crecimiento del sector']
      },
      improvement_opportunities: [{
        title: 'Mejorar presencia digital',
        description: 'Fortalecer la presencia online y marketing digital.',
        priority: 'alta',
        impact_potential: '+20% valoración',
        time_horizon: '6-12 meses',
        estimated_investment: 'Medio'
      }],
      sector_overview: {
        market_size: 'Crecimiento moderado',
        growth_rate: 8.5,
        key_trends: ['Digitalización']
      },
      generated_at: null,
      source: 'fallback_minimal'
    };
  };

  // Helper to translate competitor types and names
  const translateCompetitorInfo = (competitor) => {
    let translatedName = competitor.name;
    
    // Handle name translations for common patterns
    if (competitor.name.includes('Líder')) {
      translatedName = language === 'en' 
        ? competitor.name.replace('Líder', t('results.competitive.labels.leader'))
        : competitor.name;
    }
    if (competitor.name.includes('Competidor Principal')) {
      translatedName = language === 'en' 
        ? competitor.name.replace('Competidor Principal', t('results.competitive.labels.mainCompetitor'))
        : competitor.name;
    }
    if (competitor.name.includes('Competidor Similar')) {
      translatedName = language === 'en' 
        ? competitor.name.replace('Competidor Similar', t('results.competitive.labels.similarCompetitor'))
        : competitor.name;
    }
    if (competitor.name.includes('Tu Empresa')) {
      translatedName = language === 'en' 
        ? competitor.name.replace('Tu Empresa', t('results.competitive.labels.yourCompany'))
        : competitor.name;
    }

    return translatedName;
  };

  // Helper to translate opportunity data
  const translateOpportunity = (opportunity) => {
    const priorityMapping = {
      'alta': t('results.competitive.opportunities.priority.alta'),
      'media': t('results.competitive.opportunities.priority.media'), 
      'baja': t('results.competitive.opportunities.priority.baja'),
      'crítica': t('results.competitive.opportunities.priority.crítica'),
      'high': t('results.competitive.opportunities.priority.high'),
      'medium': t('results.competitive.opportunities.priority.medium'),
      'low': t('results.competitive.opportunities.priority.low'),
      'critical': t('results.competitive.opportunities.priority.crítica')
    };

    // Map common opportunity titles and descriptions
    let translatedTitle = opportunity.title;
    let translatedDescription = opportunity.description;

    if (language === 'en') {
      // Translate common Spanish titles to English
      const titleMappings = {
        'Acelerar Innovación Tecnológica': t('results.competitive.opportunities.items.accelerateInnovation.title'),
        'Optimizar Escalabilidad': t('results.competitive.opportunities.items.optimizeScalability.title'),
        'Oportunidad IA #1': t('results.competitive.opportunities.items.aiOpportunity1.title'),
        'Fortalecer Presencia Digital': t('results.competitive.opportunities.items.strengthenDigitalPresence.title'),
        'Mejorar Diferenciación Competitiva': t('results.competitive.opportunities.items.improveCompetitiveDifferentiation.title'),
        'Expandir Base de Clientes': t('results.competitive.opportunities.items.expandClientBase.title'),
        'Mejorar Experiencia del Cliente': t('results.competitive.opportunities.items.enhanceCustomerExperience.title'),
        'Desarrollar Alianzas Estratégicas': t('results.competitive.opportunities.items.developStrategicPartnerships.title'),
        'Optimizar Eficiencia Operacional': t('results.competitive.opportunities.items.optimizeOperationalEfficiency.title'),
        'Invertir en Desarrollo de Talento': t('results.competitive.opportunities.items.investInTalentDevelopment.title'),
        'Expandir Geográficamente': t('results.competitive.opportunities.items.expandGeographically.title'),
        'Diversificar Fuentes de Ingresos': t('results.competitive.opportunities.items.diversifyRevenue.title'),
        'Mejorar presencia digital': t('results.competitive.opportunities.items.strengthenDigitalPresence.title'),
        'Contactar para análisis personalizado': 'Contact for personalized analysis',
        'Fortalecer presencia digital y marca online': t('results.competitive.opportunities.items.strengthenDigitalPresence.title'),
        'Monitorear competidores líderes regularmente': 'Monitor leading competitors regularly',
        // Casos adicionales vistos en la captura
        'Fortalecer diferenciación competitiva': t('results.competitive.opportunities.items.improveCompetitiveDifferentiation.title'),
        'Monitorear competidores principales': t('results.competitive.opportunities.items.monitorCompetitors.title'),
        'Strengthen Digital Presence': 'Strengthen Digital Presence', // Ya en inglés
        'Accelerar diferenciación competitiva': t('results.competitive.opportunities.items.improveCompetitiveDifferentiation.title')
      };
      
      const descMappings = {
        'Invertir en I+D y nuevas tecnologías emergentes': t('results.competitive.opportunities.items.accelerateInnovation.desc'),
        'Mejorar arquitectura técnica para soportar crecimiento': t('results.competitive.opportunities.items.optimizeScalability.desc'),
        'Expansión a nuevos segmentos': t('results.competitive.opportunities.items.aiOpportunity1.desc'),
        'Mejorar visibilidad online y engagement en redes sociales': t('results.competitive.opportunities.items.strengthenDigitalPresence.desc'),
        'Desarrollar propuestas de valor únicas que te distingan de competidores': t('results.competitive.opportunities.items.improveCompetitiveDifferentiation.desc'),
        'Implementar estrategias para atraer y retener nuevos clientes': t('results.competitive.opportunities.items.expandClientBase.desc'),
        'Optimizar puntos de contacto y calidad de servicio para aumentar satisfacción': t('results.competitive.opportunities.items.enhanceCustomerExperience.desc'),
        'Construir partnerships que puedan acelerar crecimiento y alcance de mercado': t('results.competitive.opportunities.items.developStrategicPartnerships.desc'),
        'Optimizar procesos para reducir costos y mejorar productividad': t('results.competitive.opportunities.items.optimizeOperationalEfficiency.desc'),
        'Construir capacidades y habilidades para soportar crecimiento del negocio': t('results.competitive.opportunities.items.investInTalentDevelopment.desc'),
        'Entrar a nuevos mercados para diversificar fuentes de ingresos': t('results.competitive.opportunities.items.expandGeographically.desc'),
        'Reducir dependencia de fuentes únicas de ingresos': t('results.competitive.opportunities.items.diversifyRevenue.desc'),
        'Recomendación estratégica basada en análisis competitivo.': 'Strategic recommendation based on competitive analysis.',
        'Fortalecer la presencia online y marketing digital.': 'Strengthen online presence and digital marketing.'
      };

      translatedTitle = titleMappings[opportunity.title] || opportunity.title;
      translatedDescription = descMappings[opportunity.description] || opportunity.description;
    }

    return {
      ...opportunity,
      title: translatedTitle,
      description: translatedDescription,
      priority: priorityMapping[opportunity.priority] || opportunity.priority,
      time_horizon: opportunity.time_horizon?.replace(/(\d+)[-–](\d+)\s*meses?/g, (match, start, end) => {
        return language === 'en' 
          ? t('results.competitive.opportunities.timeMonths', { range: `${start}–${end}` })
          : match;
      }) || opportunity.time_horizon
    };
  };

  // Helper to translate AI insights
  const translateInsight = (insight) => {
    if (language !== 'en') return insight;

    const insightMappings = {
      'Posicionamiento sólido en el mercado': t('results.competitive.insights.bullets.solidPositioning'),
      'Propuesta de valor diferenciada': t('results.competitive.insights.bullets.differentiatedValue'),
      'Equipo experimentado': t('results.competitive.insights.bullets.experiencedTeam'),
      'Escala limitada comparada con líderes': t('results.competitive.insights.bullets.limitedScale'),
      'Recursos financieros restringidos': t('results.competitive.insights.bullets.financialConstraints'),
      'Fuerte reconocimiento de marca': t('results.competitive.insights.bullets.strongBrandRecognition'),
      'Fuentes de ingresos diversificadas': t('results.competitive.insights.bullets.diversifiedRevenue'),
      'Base sólida de clientes': t('results.competitive.insights.bullets.solidClientBase'),
      'Estrategia de precios competitiva': t('results.competitive.insights.bullets.competitivePricing'),
      'Alta eficiencia operacional': t('results.competitive.insights.bullets.operationalEfficiency'),
      'Cartera de productos innovadores': t('results.competitive.insights.bullets.innovativeProducts'),
      'Posición de liderazgo en el mercado': t('results.competitive.insights.bullets.marketLeadership'),
      'Equipo altamente calificado': t('results.competitive.insights.bullets.qualifiedTeam'),
      'Reputación establecida en el mercado': t('results.competitive.insights.bullets.establishedReputation'),
      'Estructura organizacional flexible': t('results.competitive.insights.bullets.flexibleStructure'),
      'Ventaja competitiva clara': t('results.competitive.insights.bullets.competitiveAdvantage'),
      'Fuerte potencial de crecimiento': t('results.competitive.insights.bullets.growthPotential'),
      'Capacidades digitales avanzadas': t('results.competitive.insights.bullets.digitalCapabilities'),
      'Alta lealtad de clientes': t('results.competitive.insights.bullets.customerLoyalty'),
      'Estrategias de marketing efectivas': t('results.competitive.insights.bullets.effectiveMarketing'),
      'Recursos financieros limitados': t('results.competitive.insights.bullets.limitedResources'),
      'Desafíos de saturación del mercado': t('results.competitive.insights.bullets.marketSaturation'),
      'Limitaciones de escalabilidad': t('results.competitive.insights.bullets.scalabilityIssues'),
      'Competencia intensa': t('results.competitive.insights.bullets.competitiveIntensity'),
      'Brechas tecnológicas': t('results.competitive.insights.bullets.technologicalGaps'),
      'Dependencia de clientes clave': t('results.competitive.insights.bullets.dependencyOnKeyClients'),
      'Alcance geográfico limitado': t('results.competitive.insights.bullets.limitedGeographicReach'),
      'Brechas en reconocimiento de marca': t('results.competitive.insights.bullets.brandAwarenessGaps'),
      'Ineficiencias operacionales': t('results.competitive.insights.bullets.operationalInefficiencies'),
      'Desafíos de retención de talento': t('results.competitive.insights.bullets.talentRetention'),
      'Restricciones regulatorias': t('results.competitive.insights.bullets.regulatoryConstraints'),
      'Exposición a volatilidad del mercado': t('results.competitive.insights.bullets.marketVolatility'),
      'Posicionamiento competitivo': t('results.competitive.insights.bullets.solidPositioning'),
      'Oportunidades de mercado': 'Market opportunities',
      'Competencia sectorial': t('results.competitive.insights.bullets.competitiveIntensity'),
      'Crecimiento del mercado': 'Market growth',
      'Análisis en desarrollo': 'Analysis in development',
      'Potencial de crecimiento': t('results.competitive.insights.bullets.growthPotential'),
      'Agilidad competitiva': 'Competitive agility',
      // Casos específicos vistos en la captura
      'Competencia sectorial intensa': t('results.competitive.insights.bullets.sectorCompetitionIntense'),
      'Líderes con mayor cuota de mercado': t('results.competitive.insights.bullets.marketLeadersHigherShare'),
      'Presión sobre márgenes': t('results.competitive.insights.bullets.marginPressure'),
      'Solid market positioning': t('results.competitive.insights.bullets.solidPositioning'), // Ya en inglés
      'Market opportunities': 'Market opportunities' // Ya en inglés
    };

    return insightMappings[insight] || insight;
  };

  // Helper to translate competitive position badge
  const translateBadgeDescription = (description) => {
    // Badge state mapping from backend description to i18n keys
    const BADGE_KEY_BY_DESCRIPTION = {
      'Posición competitiva sólida': 'results.competitive.badge.solid',
      'Posición competitiva débil': 'results.competitive.badge.weak', 
      'Posición competitiva media': 'results.competitive.badge.average',
      'Posición competitiva fuerte': 'results.competitive.badge.strong',
      'Solid competitive position': 'results.competitive.badge.solid',
      'Weak competitive position': 'results.competitive.badge.weak',
      'Average competitive position': 'results.competitive.badge.average', 
      'Strong competitive position': 'results.competitive.badge.strong'
    };

    // If we have a description from backend, try to map it to i18n
    if (description) {
      const mappedKey = BADGE_KEY_BY_DESCRIPTION[description];
      if (mappedKey) {
        return t(mappedKey);
      }
      // If no exact match, try partial matching for common patterns
      if (description.toLowerCase().includes('sólida') || description.toLowerCase().includes('solid')) {
        return t('results.competitive.badge.solid');
      }
      if (description.toLowerCase().includes('débil') || description.toLowerCase().includes('weak')) {
        return t('results.competitive.badge.weak');
      }
      if (description.toLowerCase().includes('media') || description.toLowerCase().includes('average')) {
        return t('results.competitive.badge.average');
      }
      if (description.toLowerCase().includes('fuerte') || description.toLowerCase().includes('strong')) {
        return t('results.competitive.badge.strong');
      }
    }

    // Fallback to solid if no description or no match
    return t('results.competitive.badge.solid');
  };
  
  // State management
  const [benchmarkingData, setBenchmarkingData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [error, setError] = useState(null);
  
  // Feature flag for live analysis (can be controlled via environment)
  const ENABLE_LIVE_ANALYSIS = process.env.REACT_APP_ENABLE_LIVE_COMPETITORS !== 'false';
  
  // Timeout configuration for requests (1.8 seconds as specified)
  const LIVE_ANALYSIS_TIMEOUT = 1800; // 1.8 seconds in milliseconds
  const FALLBACK_TIMEOUT = 5000; // 5 seconds for fallback
  
  // Ref to track component mount status to prevent setState after unmount
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  
  // Helper values
  const currentValue = result?.valorTotal || result?.totalValue || 0;
  const hasValidResult = currentValue > 0;

  // Normalize benchmarking data whenever it changes
  const normalizedData = normalizeData(benchmarkingData);

  // Cleanup function - only run on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []); // Empty dependency array ensures this only runs on mount/unmount

  // Helper to create fetch with timeout
  const fetchWithTimeout = async (url, options, timeoutMs) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (controller.signal.aborted) {
        throw new Error(`Request timeout after ${timeoutMs}ms`);
      }
      throw error;
    }
  };

  // Generar análisis competitivo
  const generateCompetitiveAnalysis = useCallback(async () => {
    if (isLoading || !hasValidResult) return;
    
    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    
    if (!isMountedRef.current) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      let backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      if (!backendUrl) {
        throw new Error('Backend URL not configured');
      }
      
      // Ensure backend URL is absolute and remove any trailing slash
      backendUrl = backendUrl.replace(/\/+$/, '');
      
      // If backend URL is relative or doesn't start with http, make it absolute
      if (!backendUrl.startsWith('http')) {
        // Get current origin and use it as base
        const currentOrigin = window.location.origin;
        backendUrl = backendUrl.startsWith('/') 
          ? `${currentOrigin}${backendUrl}` 
          : `${currentOrigin}/${backendUrl}`;
      }
      
      // Only try live analysis if feature flag is enabled
      if (ENABLE_LIVE_ANALYSIS) {
        console.log('🔍 Intentando análisis de competidores en vivo...');
        
        // PASO 1: Intentar primero el endpoint de competidores en vivo con timeout de 1.8s
        try {
          const liveParams = new URLSearchParams({
            company: formData?.companyName || formData?.name || 'Mi Empresa',
            sector: formData?.sector || formData?.industry || 'technology',
            country: 'España',
            language: language,
            revenue: (formData?.revenue || 0).toString()
          });
          
          console.log('🚀 Iniciando request live con timeout de 1.8s...');
          
          const liveResponse = await fetchWithTimeout(
            `${backendUrl}/api/competitors-live?${liveParams}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
              signal: abortControllerRef.current.signal
            },
            LIVE_ANALYSIS_TIMEOUT
          );
          
          // Check if component is still mounted before processing response
          if (!isMountedRef.current) return;
          
          if (liveResponse.ok) {
            const liveData = await liveResponse.json();
            
            if (!isMountedRef.current) return;
            
            console.log('✅ Respuesta live recibida:', liveData.source);
            
            // Verificar si es análisis en vivo real o fallback
            const isLiveAnalysis = liveData.source === 'live_analysis' && liveData.lastUpdatedAt;
            
            console.log(isLiveAnalysis ? 
              '✅ Análisis de competidores EN VIVO generado' : 
              '⚠️ Usando análisis de fallback desde endpoint live'
            );
            
            setBenchmarkingData(liveData);
            setIsLoading(false);
            return; // Éxito con análisis en vivo
          }
        } catch (liveError) {
          if (liveError.name === 'AbortError') {
            console.log('🚫 Live request was aborted');
            return;
          }
          console.log('⚠️ Análisis en vivo falló (timeout o error):', liveError.message);
          // Continuar con el método tradicional
        }
      }
      
      // Check if component is still mounted before fallback
      if (!isMountedRef.current) return;
      
      // PASO 2: Fallback al endpoint tradicional de análisis competitivo
      console.log('🏆 Fallback: Iniciando análisis competitivo tradicional...');
      
      const requestData = {
        form_data: formData ?? {},
        current_valuation: currentValue
      };
      
      const response = await fetchWithTimeout(
        `${backendUrl}/api/competitive-analysis`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
          signal: abortControllerRef.current.signal
        },
        FALLBACK_TIMEOUT
      );
      
      console.log('📊 Respuesta tradicional recibida:', response.status, response.statusText);
      
      if (!isMountedRef.current) return;
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      if (!isMountedRef.current) return;
      
      console.log('✅ Análisis competitivo tradicional generado');
      setBenchmarkingData(data);
      
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log('🚫 Request was aborted');
        return;
      }
      
      if (!isMountedRef.current) return;
      
      console.error('❌ Error generating competitive analysis:', err);
      
      // Check if it's a 404 error (URL construction issue) or any API error
      if (err.message.includes('404') || err.message.includes('page not found') || 
          err.message.includes('Failed to fetch') || err.message.includes('timeout')) {
        console.log('🔄 API error detected, using silent fallback archetype data...');
        
        // Create fallback archetype data silently (no error notification)
        const fallbackData = {
          competitive_position: {
            market_position: {
              percentile: 75,
              grade: 'B+',
              ranking: `#2`,
              revenue_multiple: 1.2
            },
            strengths: [
              language === 'es' ? 'Sólida posición en el mercado' : 'Solid market position',
              language === 'es' ? 'Buenos fundamentos financieros' : 'Strong financial fundamentals',
              language === 'es' ? 'Experiencia del equipo' : 'Team expertise'
            ],
            weaknesses: [
              language === 'es' ? 'Presencia digital limitada' : 'Limited digital presence',
              language === 'es' ? 'Competencia creciente' : 'Growing competition'
            ]
          },
          competitor_comparison: {
            total_competitors_analyzed: 5,
            market_share: 15.8,
            avg_revenue: 650000
          },
          improvement_opportunities: [
            {
              category: language === 'es' ? 'Marketing Digital' : 'Digital Marketing',
              description: language === 'es' ? 'Expandir presencia online' : 'Expand online presence',
              impact_potential: language === 'es' ? '+20% valoración' : '+20% valuation',
              urgency: 'medium'
            }
          ],
          ai_insights: [
            language === 'es' ? 
              'Posición competitiva sólida con oportunidades de crecimiento digital.' : 
              'Solid competitive position with digital growth opportunities.'
          ],
          generated_at: new Date().toISOString(),
          source: 'fallback_archetype'
        };
        
        setBenchmarkingData(fallbackData);
        setIsLoading(false);
        // NO set error - silent fallback
        return;
      }
      
      // Only show error for non-recoverable errors (keep this minimal and rare)
      console.error('❌ Non-recoverable error:', err);
      // For critical errors, don't show any UI error - just log and use fallback
      // setError(`${t('results.competitive.error.generate', { defaultValue: 'Error generating competitive analysis' })}: ${err.message}`);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [currentValue, formData, hasValidResult, language, ENABLE_LIVE_ANALYSIS]); // Added ENABLE_LIVE_ANALYSIS to deps

  // Generar análisis al montar el componente si hay datos válidos
  useEffect(() => {
    if (hasValidResult && !benchmarkingData && !isLoading && isMountedRef.current) {
      console.log('🚀 Triggering automatic competitive analysis...');
      // Add a small delay to avoid rapid re-triggers and ensure single execution
      setTimeout(() => {
        // Double-check conditions to prevent duplicate requests
        if (hasValidResult && !benchmarkingData && !isLoading && isMountedRef.current) {
          generateCompetitiveAnalysis();
        }
      }, 100);
    }
  }, [hasValidResult]); // Simplified dependencies - only trigger when hasValidResult changes

  // Componente para estado sin resultado
  if (!hasValidResult) {
    return (
      <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-6 border border-purple-500/30">
        <div className="text-center">
          <div className="text-4xl mb-3">🏆</div>
          <h3 className="text-xl font-bold text-white mb-3">{t('results.competitive.title')}</h3>
          <p className="text-gray-300 mb-4">
            {t('results.competitive.description')}
          </p>
          <div className="text-sm text-gray-400">
            {t('results.competitive.includes')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-2xl p-6 border border-purple-500/30">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <span className="text-2xl mr-2">🏆</span>
          {t('results.competitive.completeTitle')}
        </h3>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm transition-all duration-300"
        >
          {isExpanded ? t('results.competitive.collapse') : t('results.competitive.viewDetails')}
        </button>
      </div>

      {/* Estado de carga - Skeleton loader estático */}
      {isLoading && (
        <div className="space-y-6 animate-pulse">
          {/* Skeleton para resumen de posición competitiva */}
          <div className="bg-slate-700/30 p-5 rounded-lg">
            <div className="h-6 bg-purple-400/30 rounded mb-3 w-48"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="h-8 bg-white/20 rounded mb-2 w-12 mx-auto"></div>
                <div className="h-4 bg-gray-300/20 rounded w-24 mx-auto"></div>
              </div>
              <div className="text-center">
                <div className="h-8 bg-white/20 rounded mb-2 w-8 mx-auto"></div>
                <div className="h-4 bg-gray-300/20 rounded w-20 mx-auto"></div>
              </div>
              <div className="text-center">
                <div className="h-8 bg-white/20 rounded mb-2 w-8 mx-auto"></div>
                <div className="h-4 bg-gray-300/20 rounded w-16 mx-auto"></div>
              </div>
              <div className="text-center">
                <div className="h-8 bg-white/20 rounded mb-2 w-12 mx-auto"></div>
                <div className="h-4 bg-gray-300/20 rounded w-20 mx-auto"></div>
              </div>
            </div>
            <div className="mt-3 text-center">
              <div className="h-6 bg-purple-600/30 rounded-full w-48 mx-auto"></div>
            </div>
          </div>
          
          {/* Skeleton para oportunidades de mejora */}
          <div className="bg-slate-700/30 p-5 rounded-lg">
            <div className="h-6 bg-green-400/30 rounded mb-3 w-56"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-slate-600/30 p-4 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <div className="h-5 bg-white/20 rounded w-48"></div>
                    <div className="h-5 bg-red-600/20 rounded w-12"></div>
                  </div>
                  <div className="h-4 bg-gray-300/20 rounded mb-2 w-full"></div>
                  <div className="flex items-center justify-between">
                    <div className="h-3 bg-gray-400/20 rounded w-24"></div>
                    <div className="h-3 bg-gray-400/20 rounded w-20"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Loading message estático */}
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin mr-2"></div>
              <span className="text-gray-300">{t('results.competitive.generatingAnalysis')}</span>
            </div>
            <div className="text-sm text-gray-400">
              {ENABLE_LIVE_ANALYSIS ? 
                t('results.competitive.analyzingLive') || 'Analizando competidores en tiempo real...' :
                t('results.competitive.analyzingProcess')
              }
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {/* Error state - only show for critical non-recoverable errors (hidden for normal fallbacks) */}
      {false && error && (
        <div className="bg-yellow-600/10 border border-yellow-500/20 rounded-lg p-3 mb-4">
          <div className="flex items-center">
            <span className="text-yellow-400 text-sm mr-2">⚠️</span>
            <div>
              <p className="text-yellow-300 text-sm font-medium">{t('results.competitive.fallbackNote', { 
                defaultValue: language === 'es' ? 'Usando datos de referencia' : 'Using reference data' 
              })}</p>
              <p className="text-yellow-200 text-xs opacity-75">{t('results.competitive.fallbackDetail', {
                defaultValue: language === 'es' ? 'Análisis basado en benchmarks del sector' : 'Analysis based on sector benchmarks'
              })}</p>
            </div>
          </div>
        </div>
      )}

      {/* Contenido del análisis */}
      {normalizedData && (
        <div className="space-y-6">
          {/* Resumen de posición competitiva */}
          <div className="bg-slate-700/30 p-5 rounded-lg">
            <h4 className="font-semibold text-purple-400 mb-3 flex items-center">
              <span className="text-xl mr-2">📊</span>
              {t('results.competitive.yourPosition')}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">
                  {normalizedData.competitive_position?.market_position?.percentile}%
                </div>
                <p className="text-sm text-gray-300">{t('results.competitive.sectorPercentile')}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">
                  {normalizedData.competitive_position?.competitive_score?.grade || 'B+'}
                </div>
                <p className="text-sm text-gray-300">{t('results.competitive.competitiveGrade')}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">
                  #{normalizedData.competitor_comparison?.user_position || 3}
                </div>
                <p className="text-sm text-gray-300">{t('results.competitive.rankingPosition')}</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">
                  {normalizedData.competitive_position?.financial_metrics?.current_multiple?.toFixed(1)}x
                </div>
                <p className="text-sm text-gray-300">{t('results.competitive.revenueMultiple')}</p>
              </div>
            </div>
            <div className="mt-3 text-center">
              <span className="bg-purple-600/20 text-purple-300 px-3 py-1 rounded-full text-sm">
                {translateBadgeDescription(normalizedData.competitive_position?.competitive_score?.description)}
              </span>
            </div>
          </div>

          {/* Comparación con competidores - Solo expandido */}
          {isExpanded && normalizedData.competitor_comparison && (
            <div className="bg-slate-700/30 p-5 rounded-lg">
              <h4 className="font-semibold text-purple-400 mb-3 flex items-center">
                <span className="text-xl mr-2">⚔️</span>
                {t('results.competitive.competitorComparison')}
              </h4>
              <div className="space-y-3">
                {normalizedData.competitor_comparison.competitors?.map((competitor, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      competitor.type === 'user_company' 
                        ? 'bg-purple-600/20 border border-purple-500/30' 
                        : 'bg-slate-600/30'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">
                        {competitor.type === 'market_leader' ? '👑' : 
                         competitor.type === 'user_company' ? '🎯' : '🏢'}
                      </div>
                      <div>
                        <div className="font-semibold text-white">{translateCompetitorInfo(competitor)}</div>
                        {competitor.market_share && (
                          <div className="text-sm text-gray-400">
                            {t('results.competitive.labels.share')}: {formatPercentage(competitor.market_share)} | {t('results.competitive.labels.growth')}: {formatPercentage(competitor.growth_rate)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-white">
                        {formatCurrency(competitor.valuation)}
                      </div>
                      <div className="text-sm text-gray-400">
                        {competitor.type === 'user_company' ? t('results.competitive.yourPositionLabel') : 
                         competitor.type === 'market_leader' ? t('results.competitive.leader') : t('results.competitive.competitor')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Oportunidades de mejora */}
          {normalizedData.improvement_opportunities && normalizedData.improvement_opportunities.length > 0 && (
            <div className="bg-slate-700/30 p-5 rounded-lg">
              <h4 className="font-semibold text-green-400 mb-3 flex items-center">
                <span className="text-xl mr-2">💡</span>
                {t('results.competitive.improvementOpportunities')}
              </h4>
              <div className="space-y-3">
                {normalizedData.improvement_opportunities.slice(0, 3).map((opportunity, index) => {
                  const translatedOpp = translateOpportunity(opportunity);
                  return (
                    <div key={index} className="bg-slate-600/30 p-4 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-semibold text-white">{translatedOpp.title}</h5>
                        <span className={`px-2 py-1 rounded text-xs ${
                          opportunity.priority === 'alta' || opportunity.priority === 'high' ? 'bg-red-600/20 text-red-300' :
                          opportunity.priority === 'crítica' || opportunity.priority === 'critical' ? 'bg-red-700/30 text-red-200' :
                          'bg-yellow-600/20 text-yellow-300'
                        }`}>
                          {translatedOpp.priority}
                        </span>
                      </div>
                      <p className="text-gray-300 text-sm mb-2">{translatedOpp.description}</p>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>💰 {formatImpactPotential(translatedOpp.impact_potential)}</span>
                        <span>⏰ {translatedOpp.time_horizon}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {isExpanded && normalizedData.improvement_opportunities.length > 3 && (
                <div className="mt-3 text-center">
                  <span className="text-sm text-gray-400">
                    {(() => {
                      const count = normalizedData.improvement_opportunities.length - 3;
                      const key = count === 1 ? 'results.competitive.opportunities.moreIdentified_one' : 'results.competitive.opportunities.moreIdentified_other';
                      return t(key, { count });
                    })()}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Insights de IA - Solo expandido */}
          {isExpanded && normalizedData.ai_insights && (
            <div className="bg-slate-700/30 p-5 rounded-lg">
              <h4 className="font-semibold text-blue-400 mb-3 flex items-center">
                <span className="text-xl mr-2">🤖</span>
                {t('results.competitive.aiInsights')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Strengths */}
                {normalizedData.ai_insights.strengths && (
                  <div>
                    <h5 className="font-semibold text-green-400 mb-2">✅ {t('results.competitive.insights.strengths')}</h5>
                    <ul className="space-y-1 text-sm text-gray-300">
                      {normalizedData.ai_insights.strengths.slice(0, 3).map((strength, index) => (
                        <li key={index}>• {translateInsight(strength)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Weaknesses */}
                {normalizedData.ai_insights.weaknesses && (
                  <div>
                    <h5 className="font-semibold text-red-400 mb-2">⚠️ {t('results.competitive.insights.areasForImprovement')}</h5>
                    <ul className="space-y-1 text-sm text-gray-300">
                      {normalizedData.ai_insights.weaknesses.slice(0, 3).map((weakness, index) => (
                        <li key={index}>• {translateInsight(weakness)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Información del análisis */}
          <div className="text-center">
            <p className="text-xs text-gray-400">
              {(() => {
                const count = normalizedData.competitor_comparison?.total_competitors_analyzed || 
                             benchmarkingData?.competitors?.length || 4;
                
                // Always show as normal analysis - don't distinguish between live and fallback
                const analysisText = t('results.competitive.footer.analysis', { count });
                
                // Try to get date from any source, format if available
                const lastUpdated = benchmarkingData?.lastUpdatedAt || normalizedData?.generated_at;
                const formattedDate = lastUpdated ? formatDate(lastUpdated) : null;
                
                // Show standard analysis message with date if available
                if (formattedDate) {
                  return `${analysisText} • ${t('results.competitive.footer.lastUpdated', { date: formattedDate })}`;
                } else {
                  return analysisText; // Just show analysis count
                }
              })()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BenchmarkingModule;